import * as React from 'react';
import { EyeIcon, EyeOffIcon, PasswordInput, PasswordStrength } from '@repo/ui-web';
import { inputStates, passwordInputVariants, inputSizes } from '../../catalog/component-states';
import { StateToggle } from '../../components/StateToggle';

type InputState = (typeof inputStates)[number];
type Variant = (typeof passwordInputVariants)[number];
type Size = (typeof inputSizes)[number];

const strengthLabels = {
  score: ['None', 'Weak', 'Fair', 'Strong', 'Very strong'] as [string, string, string, string, string],
  minimum: 'Must be at least 8 characters.',
};

function getScore(password: string): number {
  if (!password) return 0;
  if (password.length < 4) return 1;
  if (password.length < 8) return 2;
  if (password.length < 12) return 3;
  return 4;
}

export function PasswordInputShowcase() {
  const [state, setState] = React.useState<InputState>('default');
  const [variant, setVariant] = React.useState<Variant>('default');
  const [size, setSize] = React.useState<Size>('lg');
  const [value, setValue] = React.useState('');
  const [standaloneScore, setStandaloneScore] = React.useState(2);

  const score = getScore(value);
  const meetsMinimum = score >= 2;

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <StateToggle options={inputStates} value={state} onChange={setState} label="State" />
        <StateToggle
          options={passwordInputVariants}
          value={variant}
          onChange={setVariant}
          label="Variant"
          formatLabel={(v) => (v === 'with-strength' ? 'With strength' : 'Default')}
        />
        <StateToggle options={inputSizes} value={size} onChange={setSize} label="Size" />
        <PasswordInput
          id="ds-password"
          label="Password"
          size={size}
          variant={variant}
          placeholder="Enter password"
          disabled={state === 'disabled'}
          error={state === 'error' ? 'Password does not meet requirements.' : undefined}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onClear={() => setValue('')}
          clearButtonLabel="Clear value"
          showPasswordLabel="Show password"
          hidePasswordLabel="Hide password"
          eyeIcon={<EyeIcon className={size === 'sm' ? 'size-icon-xs' : 'size-icon-sm'} />}
          eyeOffIcon={<EyeOffIcon className={size === 'sm' ? 'size-icon-xs' : 'size-icon-sm'} />}
          strengthScore={score}
          strengthMeetsMinimum={meetsMinimum}
          strengthWarning={!meetsMinimum ? 'This password is too easy to guess.' : null}
        />
      </div>

      <div className="space-y-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          PasswordStrength (standalone)
        </p>
        <div className="flex items-center gap-4">
          <label className="type-body-sm text-muted-foreground">
            Score
            <input
              type="range"
              min={0}
              max={4}
              value={standaloneScore}
              onChange={(e) => setStandaloneScore(Number(e.target.value))}
              className="ml-2"
            />
            <span className="ml-1">{standaloneScore}</span>
          </label>
        </div>
        <PasswordStrength
          score={standaloneScore}
          meetsMinimum={standaloneScore >= 2}
          labels={strengthLabels}
          label="Password strength"
          warning={standaloneScore < 2 ? 'This password is too easy to guess.' : null}
        />
      </div>
    </div>
  );
}
